import React, { useMemo, useState } from 'react'
import { Modal } from 'antd'
import isNil from 'lodash/isNil'
import { BillingProfileRecord } from '../../api/dto/client-billing-profile.dto'
import { ModalSubtitle } from './ClientBillingProfileManagement.styles'
import { RecipientRemovalNotice } from './RecipientRemovalNotice'
import { useRecipientRemoval } from './useRecipientRemoval'

interface RemoveCompanyModalProps {
  open: boolean
  record: BillingProfileRecord | null
  /** Runs the removal; false when it failed, which keeps the dialog open. */
  onRemove: (record: BillingProfileRecord) => Promise<boolean>
  onClose: () => void
}

/**
 * Confirms removing a company. Every recipient comes off it, so each gets the
 * same line the profile editor shows when an address is dropped.
 */
const RemoveCompanyModal = ({
  open,
  record,
  onRemove,
  onClose,
}: RemoveCompanyModalProps) => {
  const [isRemoving, setIsRemoving] = useState(false)

  // Removing the company stops every address it lists, CC included.
  const recipients = useMemo(
    () => [...(record?.toEmails ?? []), ...(record?.ccEmails ?? [])],
    [record]
  )

  const removal = useRecipientRemoval({
    open,
    clientPublicKey: record?.clientPublicKey ?? null,
    companyName: record?.clientName ?? 'this company',
    profileId: record?.id ?? null,
    removedEmails: recipients,
    doneLabel: 'Company removed',
  })

  const handleOk = async () => {
    if (isNil(record)) return
    setIsRemoving(true)
    try {
      const removed = await onRemove(record)
      if (!removed) return
      // Only once the company is off: access taken first would be lost for
      // nothing if the removal then failed.
      await removal.revoke()
      onClose()
    } finally {
      setIsRemoving(false)
    }
  }

  return (
    <Modal
      open={open}
      centered
      width={600}
      title={isNil(record) ? 'Remove company?' : `Remove ${record.clientName}?`}
      okText="Remove"
      okButtonProps={{ danger: true, disabled: removal.isChecking }}
      cancelText="Cancel"
      confirmLoading={isRemoving || removal.isRevoking}
      onOk={handleOk}
      onCancel={onClose}
      destroyOnClose
    >
      <ModalSubtitle>
        {isNil(record) ? 'This company' : record.clientName} will stop getting
        funding invoices. You can find it later under Removed.
      </ModalSubtitle>

      <RecipientRemovalNotice
        lines={removal.lines}
        canRevoke={removal.canRevoke}
      />
    </Modal>
  )
}

export { RemoveCompanyModal }
